import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMDialog } from "../PMDialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

const FETCH_URL = "/users/";

export const EmployeeSelector = (props) => {
  const {
    setFieldValue,
    errors,
    values,
    onChange,
    showField,
    noLabel,
  } = props;

  const name = props?.name ?? "user_id";
  const label = props?.label ?? "Employee";
  // Opt-in: also include innleid (hired, non-employee) users in planning/resource contexts.
  const includeHired = props?.includeHired === true;

  const { t } = useTranslation();

  const excludeUserIdSet = useMemo(() => {
    const raw = props.excludeUserIds;
    const list = Array.isArray(raw)
      ? raw
      : raw != null && raw !== ""
        ? [raw]
        : [];
    return new Set(
      list
        .filter((id) => id != null && id !== "")
        .map((id) => String(id))
    );
  }, [props.excludeUserIds]);

  const baseFilters = useMemo(() => {
    const f = [
      { name: includeHired ? "EMPLOYEE_OR_HIRED" : "EMPLOYEE", value: true },
    ];
    if (props?.filters?.length) {
      props.filters.forEach((item) => f.push(item));
    }
    return f;
  }, [props.filters, includeHired]);

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);

  // Allow the dialog to be opened/closed from a parent (controlled `open` prop),
  // mirroring ProjectSelector. Lets callers render their own trigger.
  useEffect(() => {
    if (props.open === true) {
      setOpen(true);
    } else if (props.open === false) {
      setOpen(false);
    }
  }, [props.open]);

  // ── Search & list state ───────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open);

  // ── Selected state ────────────────────────────────────────────────────────
  const [selected, setSelected] = useState({
    id: null,
    field1: "Ingen valgt",
    hired: false,
  });

  useEffect(() => {
    if (props?.selected?.id) {
      setSelected({
        id: props.selected.id,
        field1: props.selected.displayTitle ?? "Missing title",
        hired: props.selected.user_type_id === 3,
      });
    }
  }, [props.selected]);

  // ── Open/close ────────────────────────────────────────────────────────────
  const handleOpen = () => {
    if (props.disabled) return;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
    props.onClose?.(false);
  };

  // ── Propagate selection ───────────────────────────────────────────────────
  const handleSelect = (item) => {
    setSelected({
      id: item.object?.id ?? item.id,
      field1: item.object?.displayTitle ?? item.primary ?? "Ingen valgt",
      hired: (item.object?.user_type_id ?? item.user_type_id) === 3,
    });

    const userId = item.object?.id ?? item.id;
    const userObject = item.object ?? item;

    if (setFieldValue) setFieldValue(name, userId);
    if (onChange) {
      onChange({
        target: { name, value: userId, object: userObject },
      });
    }
    handleClose();
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchItems = useCallback(
    async (searchQuery) => {
      setLoading(true);
      try {
        const response = await axios({
          method: "get",
          url: FETCH_URL,
          params: {
            query: searchQuery || undefined,
            page: 1,
            per_page: 20,
            sortBy: "id",
            sort: "desc",
            filters: baseFilters,
            fields: "id,displayTitle,user_type_id",
            showInDropdown: true,
          },
        });

        const array = [
          { id: null, value: null, primary: "Ingen valgt", secondary: null },
        ];
        if (response.data?.data?.length > 0) {
          response.data.data.forEach((row) => {
            if (
              row?.id != null &&
              excludeUserIdSet.has(String(row.id))
            ) {
              return;
            }
            array.push({
              id: row.id,
              value: row.id,
              primary: row.displayTitle ?? "",
              secondary: null,
              object: row,
            });
          });
        }
        setItems(array);
      } catch {
        setItems([
          { id: null, value: null, primary: "Ingen valgt", secondary: null },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [excludeUserIdSet, baseFilters]
  );

  const debouncedFetch = useCallback(
    debounce((q) => fetchItems(q), 300),
    [fetchItems]
  );

  useEffect(() => {
    if (!open) return;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, debouncedFetch]);

  // ── Trigger button ────────────────────────────────────────────────────────
  const hasSelection = selected?.id != null;

  const triggerButton =
    showField !== false ? (
      <div>
        {!noLabel && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(label)}
          </label>
        )}
        <button
          type="button"
          disabled={props.disabled}
          onClick={handleOpen}
          className={[
            "relative w-full flex items-center justify-between",
            "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
            "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
            props.disabled
              ? "cursor-not-allowed border-gray-200 text-gray-400"
              : hasSelection
              ? "border-blue-400 text-gray-900 hover:border-blue-500"
              : "border-gray-300 text-gray-500 hover:border-gray-400",
          ].join(" ")}
        >
          <span className="flex min-w-0 items-center">
            <span className="truncate">{selected.field1}</span>
            {selected.hired && (
              <span className="ml-2 flex-shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                {t("employeeSelector.hired_badge")}
              </span>
            )}
          </span>
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
        </button>
        {errors?.[name] && (
          <p className="mt-1 text-xs text-red-600">{errors[name]}</p>
        )}
      </div>
    ) : null;

  // ── Dialog content ────────────────────────────────────────────────────────
  const dialogContent = (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">{t(label)}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk etter ansatt..."
            autoFocus
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {loading ? (
          <SelectorListSkeleton />
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((item) => {
              const isSelected = item.id === selected?.id;
              const isHired = item.object?.user_type_id === 3;
              return (
                <li
                  key={item.id ?? "none"}
                  onClick={() => handleSelect(item)}
                  className={[
                    "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                    isSelected
                      ? "bg-blue-50 text-blue-700"
                      : "hover:bg-gray-50 text-gray-900",
                  ].join(" ")}
                >
                  <div className="flex items-center min-w-0">
                    <p className="text-sm font-medium truncate">{item.primary}</p>
                    {isHired && (
                      <span className="ml-2 flex-shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        {t("employeeSelector.hired_badge")}
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <>
      {triggerButton}
      <PMDialog
        open={open}
        onClose={handleClose}
        size="md"
        dense
        zIndex={props.zIndex}
        closeOnOutsideClick
      >
        {dialogContent}
      </PMDialog>
    </>
  );
};
